import { useContext, useDebugValue, useEffect, useMemo, useRef, useState } from "react"
import { ReactReduxContext } from "react-redux";
import { ITmdState } from "src/reducers"

export interface ISelector<TData> {
    (state: ITmdState): TData
}

export interface ISelectorMutate<TData, TResult> {
    (data: TData): TResult
}

export interface ISelectorCompare<TData> {
    (a: TData, b: TData): boolean;
}

export interface ISelectorOptions<TData, TResult> {
    compareFn?: ISelectorCompare<TData>;
    mutateFn?: ISelectorMutate<TData, TResult>;
}

const useSelector = <TData, TResult = TData>(selector: ISelector<TData>, options?: ISelectorOptions<TData, TResult>) => {
    const equalityFn = useMemo(() => {
        if (options?.compareFn) {
            return options.compareFn
        }

        return (a: TData, b: TData) => {

            return a === b;
        };
    }, [options?.compareFn])

    if (process.env.NODE_ENV !== 'production') {
        if (!selector) {
            throw new Error(`You must pass a selector to useSelector`)
        }

        if (typeof selector !== 'function') {
            throw new Error(`You must pass a function as a selector to useSelector`)
        }
    }

    
    const context = useContext(ReactReduxContext);

    const firstRun = useRef(true);

    const [selectedState, setSelectedState] = useState<TData>(() => {
        return selector(context.store.getState());
    });

    useEffect(() => {
        const unsubscribe = context.store.subscribe(() => {
            setSelectedState(current => {
                const newValue = selector(context.store.getState())

                if (!equalityFn(current, newValue)) {
                    return newValue;
                }

                return current;
            })
        });

        return () => {
            unsubscribe();
        }
    }, [equalityFn, selector, context.store.subscribe, context.store.getState, setSelectedState]);

    const [mutatedState, setMutateState] = useState(() => {
        if (options?.mutateFn) {
            return options.mutateFn(selectedState);
        }

        return selectedState as unknown as TResult;
    })

    useEffect(() => {
        setMutateState(() => {
            if (options?.mutateFn) {
                return options.mutateFn(selectedState);
            }
    
            return selectedState as unknown as TResult;
        })
    }, [selectedState, options?.mutateFn, setMutateState])

    useDebugValue(mutatedState);

    return mutatedState;
}

export default useSelector;